import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { syncDebugEmailFromUrl } from "./utils/api";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { MockApiTester } from "./components/MockApiTester";
import { RoleSwitcher } from "./components/RoleSwitcher";
import { Loader } from "./components/ui/Loader";
import { MouseFollower } from "./components/ui/MouseFollower";

// Lazy load authentication pages
const LandingPage = React.lazy(() =>
  import("./pages/LandingPage").then((module) => ({
    default: module.LandingPage,
  })),
);
const LoginPage = React.lazy(() =>
  import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })),
);
const ForgotPasswordPage = React.lazy(() =>
  import("./pages/ForgotPasswordPage").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = React.lazy(() =>
  import("./pages/ResetPasswordPage").then((module) => ({
    default: module.ResetPasswordPage,
  })),
);

// Lazy load dashboard pages
const DashboardPage = React.lazy(() =>
  import("./pages/dashboard/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);
const EmployeesPage = React.lazy(() =>
  import("./pages/dashboard/EmployeesPage").then((module) => ({
    default: module.EmployeesPage,
  })),
);
const EmployeeProfilePage = React.lazy(() =>
  import("./pages/dashboard/EmployeeProfilePage").then((module) => ({
    default: module.EmployeeProfilePage,
  })),
);
const AttendancePage = React.lazy(() =>
  import("./pages/dashboard/AttendancePage").then((module) => ({
    default: module.AttendancePage,
  })),
);
const LeavePage = React.lazy(() =>
  import("./pages/dashboard/LeavePage").then((module) => ({
    default: module.LeavePage,
  })),
);
const ShiftsPage = React.lazy(() =>
  import("./pages/dashboard/ShiftsPage").then((module) => ({
    default: module.ShiftsPage,
  })),
);
const AnalyticsPage = React.lazy(() =>
  import("./pages/dashboard/AnalyticsPage").then((module) => ({
    default: module.AnalyticsPage,
  })),
);
const SettingsPage = React.lazy(() =>
  import("./pages/dashboard/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const ProfilePage = React.lazy(() =>
  import("./pages/dashboard/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const AdminUserManagementPage = React.lazy(() =>
  import("./pages/dashboard/AdminUserManagementPage").then((module) => ({
    default: module.AdminUserManagementPage,
  })),
);
const AdminRolesPage = React.lazy(() =>
  import("./pages/dashboard/AdminRolesPage").then((module) => ({
    default: module.AdminRolesPage,
  })),
);
const TimesheetPage = React.lazy(() =>
  import("./pages/dashboard/TimesheetPage").then((module) => ({
    default: module.TimesheetPage,
  })),
);
const ProjectsPage = React.lazy(() =>
  import("./pages/dashboard/ProjectsPage").then((module) => ({
    default: module.ProjectsPage,
  })),
);
const ProjectDetailsPage = React.lazy(() =>
  import("./pages/dashboard/ProjectDetailsPage").then((module) => ({
    default: module.ProjectDetailsPage,
  })),
);
const DepartmentsPage = React.lazy(() =>
  import("./pages/dashboard/DepartmentsPage").then((module) => ({
    default: module.DepartmentsPage,
  })),
);
const CompaniesPage = React.lazy(() =>
  import("./pages/super-admin/CompanyManagementPage").then((module) => ({
    default: module.CompanyManagementPage,
  })),
);
const HolidaysPage = React.lazy(() =>
  import("./pages/HolidaysPage").then((module) => ({
    default: module.HolidaysPage,
  })),
);
const LeaveConfigPage = React.lazy(() =>
  import("./pages/dashboard/LeaveConfigPage").then((module) => ({
    default: module.LeaveConfigPage,
  })),
);
const LeaveCreditPage = React.lazy(() =>
  import("./pages/dashboard/LeaveCreditPage").then((module) => ({
    default: module.LeaveCreditPage,
  })),
);
const AnnouncementsPage = React.lazy(
  () => import("./pages/dashboard/AnnouncementsPage"),
);
const ReportsPage = React.lazy(() =>
  import("./pages/dashboard/ReportsPage").then((module) => ({
    default: module.ReportsPage,
  })),
);
const FinanceOperationPage = React.lazy(() =>
  import("./pages/dashboard/FinanceOperationPage").then((module) => ({
    default: module.FinanceOperationPage,
  })),
);
const BiometricPage = React.lazy(() =>
  import("./pages/dashboard/BiometricPage").then((module) => ({
    default: module.BiometricPage,
  })),
);
const OrgTreePage = React.lazy(() =>
  import("./pages/dashboard/OrgTreePage").then((module) => ({
    default: module.OrgTreePage,
  })),
);

// Loading component for lazy-loaded routes
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-navy-950">
    <Loader variant="spinner" size="lg" label="Loading..." />
  </div>
);

function App() {
  const location = useLocation();

  useEffect(() => {
    syncDebugEmailFromUrl();
  }, [location.search]);

  return (
    <>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Authentication routes */}
          <Route path="/home" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          {/* Public registration disabled */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="employees/:id" element={<EmployeeProfilePage />} />
            <Route
              path="attendance"
              element={
                <ProtectedRoute requiredPermission="view_reports">
                  <AttendancePage />
                </ProtectedRoute>
              }
            />
            <Route path="leave" element={<LeavePage />} />
            <Route path="leave/config" element={<LeaveConfigPage />} />
            <Route path="leave/credit" element={<LeaveCreditPage />} />
            <Route path="holidays" element={<HolidaysPage />} />
            <Route path="shifts" element={<ShiftsPage />} />
            <Route path="timesheet" element={<TimesheetPage />} />
            <Route
              path="projects"
              element={
                <ProtectedRoute requiredPermission="view_projects">
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects/:id"
              element={
                <ProtectedRoute requiredPermission="view_projects">
                  <ProjectDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route path="departments" element={<DepartmentsPage />} />
            <Route path="org-tree" element={<OrgTreePage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route
              path="finance-operation"
              element={
                <ProtectedRoute requiredPermission="view_finance_operation">
                  <FinanceOperationPage />
                </ProtectedRoute>
              }
            />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route
              path="biometric"
              element={
                <ProtectedRoute requiredPermission="view_biometric">
                  <BiometricPage />
                </ProtectedRoute>
              }
            />
            <Route path="admin/users" element={<AdminUserManagementPage />} />
            <Route
              path="admin/roles"
              element={
                <ProtectedRoute requiredPermission="manage_roles">
                  <AdminRolesPage />
                </ProtectedRoute>
              }
            />
            <Route path="admin/companies" element={<CompaniesPage />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
          </Route>

          {/* Redirect to dashboard if authenticated, otherwise to login */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>

      {/* Mock API Tester (only visible when mock API is enabled) */}
      <MockApiTester />

      {/* Role Switcher (only visible for admin users in development) */}
      <RoleSwitcher />

      {/* Mouse Pointer Follower remove if not needed MOUSE_ANIMATION */}
      {localStorage.getItem("animation") === "true" && <MouseFollower />}
    </>
  );
}

export default App;
